﻿import {defineStore} from "pinia";
import {useEnterpriseGatewayApiProxy} from "@/plugins/proxy-client-plugin.ts";
import {
    AllInvoicesQuery,
    AllPurchaseInvoicesQuery,
    DocumentType,
    Invoice,
    InvoiceRoundDto,
    InvoicesDto, PaymentMethod, PaymentRequestInvoicesQuery,
    PurchaseInvoice,
    PurchaseInvoicesDto
} from "@/api/enterprise-gateway-api.proxy.ts";
import {LoadingState} from "@/app/stores/loading-state.ts";
import {useErrorStore} from "@/app/stores/error-store.ts";
import {PagedResult} from "@/app/shared/helpers/paged-result.ts";

export interface InvoicesResponse {
    downloadUrl: string | undefined;
    pagedResult: PagedResult<Invoice>;
}

export interface PurchaseInvoicesResponse {
    downloadUrl: string | undefined;
    pagedResult: PagedResult<PurchaseInvoice>;
}

export const useInvoicingStore = defineStore('invoicing-store', () => {
    const errorStore = useErrorStore();
    const apiProxy = useEnterpriseGatewayApiProxy();
    const loadingState = new LoadingState();

    const documentTypeLabels: Partial<Record<DocumentType, string>> = {
        [DocumentType.Invoice]: 'Factuur',
        [DocumentType.PaymentProof]: 'Betaalbewijs',
        [DocumentType.PaymentRequest]: 'Betaalverzoek'
    };

    const transformResponse = (response: InvoicesDto): InvoicesResponse => {
        if (!response?.invoices?.length) {
            return  {
                downloadUrl: '',
                pagedResult: PagedResult.createEmpty<Invoice>()
            };
        }

        return {
            downloadUrl: response.downloadUrl,
            pagedResult: PagedResult.fromResponse<Invoice>(
                response.invoices,
                response.pageNumber ?? 1,
                response.pageSize ?? 1,
                response.totalCount ?? 0
            )
        };
    };

    const transformPurchaseInvoiceResponse = (response: PurchaseInvoicesDto): PurchaseInvoicesResponse => {
        if (!response?.invoices?.length) {
            return  {
                downloadUrl: '',
                pagedResult: PagedResult.createEmpty<PurchaseInvoice>()
            };
        }

        return {
            downloadUrl: response.downloadUrl,
            pagedResult: PagedResult.fromResponse<PurchaseInvoice>(
                response.invoices,
                response.pageNumber ?? 1,
                response.pageSize ?? 1,
                response.totalCount ?? 0
            )
        };
    };

    const loadInvoicesForUser = async (query: AllInvoicesQuery): Promise<InvoicesResponse | undefined> => {
        try {
            loadingState.startLoading();
            const response = await apiProxy.invoice_Search(query);
            return transformResponse(response);
        } catch (e) {
            loadingState.setError();
            await errorStore.setError(e);
        } finally {
            loadingState.stopLoading();
        }
    };

    const loadInvoicesForAdmin = async (query: AllInvoicesQuery) => {
        try {
            loadingState.startLoading();
            const response = await apiProxy.operations_InvoicesSearch(query);
            return transformResponse(response);
        } catch (e) {
            loadingState.setError();
            await errorStore.setError(e);
        } finally {
            loadingState.stopLoading();
        }
    }

    const loadPaymentRequestInvoicesForAdmin = async (query: PaymentRequestInvoicesQuery) => {
        try {
            loadingState.startLoading();
            const response = await apiProxy.operations_PaymentRequestInvoicesSearch(query);
            return transformResponse(response);
        } catch (e) {
            loadingState.setError();
            await errorStore.setError(e);
        } finally {
            loadingState.stopLoading();
        }
    }

    const loadPurchaseInvoicesForAdmin = async (query: AllPurchaseInvoicesQuery) => {
        try {
            loadingState.startLoading();
            const response = await apiProxy.operations_PurchaseInvoicesSearch(query);
            return transformResponse(response);
        } catch (e) {
            loadingState.setError();
            await errorStore.setError(e);
        } finally {
            loadingState.stopLoading();
        }
    }

    const loadInvoiceRounds = async () => {
        try {
            loadingState.startLoading();
            const result = await apiProxy.invoiceRound_GetAll(0, 50);
            return PagedResult.fromResponse<InvoiceRoundDto>(result, 1, 1, 50);

        } catch (e) {
            loadingState.setError();
            await errorStore.setError(e);
        } finally {
            loadingState.stopLoading();
        }
    }

    const getDocumentTypeLabel = (documentType?: DocumentType): string => {
        return documentType ? documentTypeLabels[documentType] ?? '' : '';
    };

    const getInvoiceTypeIcon = (documentType?: DocumentType, paymentMethod?: PaymentMethod): string => {
        if (paymentMethod === PaymentMethod.Online)
            return 'pi pi-credit-card';
        else if (paymentMethod === PaymentMethod.Credits || documentType === DocumentType.PaymentProof)
            return 'pi pi-wallet';
        else
            return 'pi pi-file';
    };

    const isTopUpType = (documentType?: DocumentType): boolean => {
        return documentType === DocumentType.PaymentProof;
    };

    return {
        isLoading: loadingState.isLoading,
        errorLoading: loadingState.errorLoading,
        loadInvoiceRounds,
        loadInvoicesForUser,
        loadInvoicesForAdmin,
        loadPaymentRequestInvoicesForAdmin,
        loadPurchaseInvoicesForAdmin,
        getDocumentTypeLabel,
        getInvoiceTypeIcon,
        isTopUpType
    }
});

